import React, { memo, useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Divider,
  Flex,
  Collapse,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import {
  MF_ACCESS_REVIEWS,
  MFCampaignDetails,
  MFCampaignReview,
  CampaignDetailsFilters,
} from "state/members/accessReviews";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Chip, CountTag, SearchInput } from "components/ds";
import CampaignMeta from "./components/CampaignMeta";
import ProgressMeter from "./components/ProgressMeter";
import ResourceCard from "./components/ResourceCard";
import ReviewFilters from "./ReviewFilters";
import ReviewResourceFilters from "./ReviewResourceFilters";
import {
  groupReviewsByStatus,
  parseReviewGroupBy,
  applyReviewFilter,
  resourceFilterOptions,
  parseReviewFilterFromParams,
  reviewFilterToParamUpdates,
  toggleFilterValue,
  hasActiveReviewFilter,
  instructionForResource,
  isComplete,
  revisionRequestedCount,
  campaignStatusPillKind,
  type ReviewGroup,
  type ReviewListFilter,
  type RiskBand,
} from "./reviewLogic";
import { campaignProgressLabel, showsAccountStats } from "./signOffCampaign";
import { isEntitlementOnly } from "pages/Members/Attestations/signOff";

interface CampaignDetailsProps {
  campaignId: string;
  initialData: MFCampaignDetails;
}

const COLLAPSE_SHADOW_ROOM = "4px";

// Collapsible status group in the resource drawer.
const ResourceGroup = memo(
  ({
    group,
    campaignId,
    applicationInstructions,
    showAccountProgress,
  }: {
    group: ReviewGroup;
    campaignId: string;
    applicationInstructions?: MFCampaignDetails["campaign"]["applicationInstructions"];
    /** false for an owner sign-off cycle, whose reviews have no accounts */
    showAccountProgress: boolean;
  }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
      <Box width="full" mb="1xl">
        <HStack
          spacing="xs"
          mb="sm"
          cursor="pointer"
          onClick={() => setIsOpen((o) => !o)}
          _hover={{ opacity: 0.85 }}
        >
          <Icon
            as={ChevronDown}
            boxSize={4}
            color="text.body.tertiary"
            transition="transform 0.2s"
            transform={isOpen ? "rotate(0deg)" : "rotate(-90deg)"}
          />
          <Text
            color="text.label.default"
            fontSize="sm"
            fontWeight={500}
            lineHeight="1"
          >
            {group.label}
          </Text>
          <CountTag count={group.reviews.length} />
        </HStack>
        <Collapse
          in={isOpen}
          animateOpacity
          style={{ margin: `-${COLLAPSE_SHADOW_ROOM}` }}
        >
          <VStack spacing="md" align="stretch" p={COLLAPSE_SHADOW_ROOM}>
            {group.reviews.map((review) => (
              <ResourceCard
                key={review.id}
                review={review}
                campaignId={campaignId}
                faded={group.faded}
                showAccountProgress={showAccountProgress}
                instruction={instructionForResource(
                  applicationInstructions,
                  review.resource?.id,
                )}
              />
            ))}
          </VStack>
        </Collapse>
      </Box>
    );
  },
);

ResourceGroup.displayName = "ResourceGroup";

// Normalise the reviews payload (flat OR grouped) into a single flat list.
const flattenReviews = (
  reviews: MFCampaignDetails["reviews"] | undefined,
): MFCampaignReview[] => {
  if (!reviews) return [];
  if (Array.isArray(reviews)) return reviews;
  const grouped = reviews as unknown as {
    in_progress?: MFCampaignReview[];
    inProgress?: MFCampaignReview[];
    ready_for_approval?: MFCampaignReview[];
    complete?: MFCampaignReview[];
  };
  return [
    ...(grouped.in_progress ?? grouped.inProgress ?? []),
    ...(grouped.ready_for_approval ?? []),
    ...(grouped.complete ?? []),
  ];
};

const CampaignDetails: React.FC<CampaignDetailsProps> = ({
  campaignId,
  initialData,
}) => {
  const { campaign, stats } = initialData;
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Reviewer-side application/resource + risk-band filter, read from the URL
  // (repo convention: filter state lives in the query string so a filtered
  // view survives refresh / can be shared).
  const reviewFilter: ReviewListFilter = useMemo(
    () => parseReviewFilterFromParams((k) => searchParams.get(k)),
    [searchParams],
  );

  // Persist a mutated filter back to the URL, leaving sort/group params intact.
  const commitFilter = (next: ReviewListFilter) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of reviewFilterToParamUpdates(next)) {
      if (value === null) params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  };

  const toggleResource = (id: string) =>
    commitFilter({
      ...reviewFilter,
      resourceIds: toggleFilterValue(reviewFilter.resourceIds, id),
    });
  const toggleRisk = (band: RiskBand) =>
    commitFilter({
      ...reviewFilter,
      riskBands: toggleFilterValue(reviewFilter.riskBands, band),
    });
  const clearResources = () =>
    commitFilter({ ...reviewFilter, resourceIds: [] });
  const clearRisks = () => commitFilter({ ...reviewFilter, riskBands: [] });

  // Preserve the review_-prefixed sort filter behaviour.
  const filters = useMemo(() => {
    const sort = searchParams.get("review_sort_by");
    const newFilters: CampaignDetailsFilters = { sort_direction: "desc" };
    if (sort) {
      newFilters.sort_by = sort as "name" | "requires_review" | "completed";
    }
    return Object.keys(newFilters).length > 1 ? newFilters : undefined;
  }, [searchParams]);

  const { data: filteredData, isLoading: reviewsLoading } =
    MF_ACCESS_REVIEWS.useGetCampaignDetails(campaignId, filters, {
      enabled: !!campaignId,
    });

  const allReviews = useMemo(
    () => flattenReviews(filteredData?.reviews ?? initialData.reviews),
    [filteredData, initialData.reviews],
  );

  // Application/resource options come from the full review list so the
  // dropdown always offers every resource in the campaign, even ones the
  // current search/filter has hidden.
  const resourceOptions = useMemo(
    () => resourceFilterOptions(allReviews),
    [allReviews],
  );

  const searched = useMemo(() => {
    const filtered = applyReviewFilter(allReviews, reviewFilter);
    const q = search.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((r) => r.resource?.name?.toLowerCase().includes(q));
  }, [allReviews, search, reviewFilter]);

  // Group-by mode (URL: review_group_by). "status" renders the status groups
  // (in progress → awaiting → complete, completed faded); "none" (the default)
  // renders one flat list in the server's sort order.
  const groupBy = parseReviewGroupBy(searchParams.get("review_group_by"));
  const groups = useMemo(
    () => (groupBy === "status" ? groupReviewsByStatus(searched) : []),
    [searched, groupBy],
  );

  // Owner sign-off cycles (PLO-5185) carry no account rows, so everything
  // reading stats.accounts would render 0/0 — the resource list stands alone.
  const showAccountProgress = showsAccountStats(campaign);

  // Require actual reviews — an empty campaign (0/0) is not "complete".
  // isComplete encodes the total > 0 guard.
  const reviewsComplete = isComplete(
    stats.completed_reviews,
    stats.total_reviews,
  );

  // Revision signal for the header (restored in PLO-4803). Count over the full
  // unfiltered review list so search/filters don't hide the warning.
  const revisionCount = useMemo(
    () => revisionRequestedCount(initialData.reviews),
    [initialData.reviews],
  );

  return (
    <VStack align="flex-start" spacing={0} width="full">
      {/* Drawer header — name + description + shared meta line + resources meter */}
      <Box
        width="full"
        px="1xl"
        pt="1xl"
        pb="lg"
        borderBottom="1px solid"
        borderBottomColor="border.default"
      >
        <Flex
          justify="space-between"
          align="flex-start"
          gap="lg"
          w="full"
          pr="2xl"
        >
          <VStack align="flex-start" spacing="sm" flex="1" minW={0}>
            <HStack spacing="sm" align="center" minW={0}>
              <Text
                color="text.body.primary"
                fontFamily="heading"
                fontSize="2xl"
                fontWeight={500}
                lineHeight="1.2"
              >
                {campaign.name}
              </Text>
              {/* Matches the campaign card's chip (PLO-5731) so the mode is
                  named on both surfaces the reviewer passes through. */}
              {isEntitlementOnly(campaign) && (
                <Chip tone="warning">Entitlement only</Chip>
              )}
            </HStack>
            {campaign.description && (
              <Text
                color="text.body.tertiary"
                fontSize="sm"
                fontWeight={400}
                lineHeight="1.5"
              >
                {campaign.description}
              </Text>
            )}
            <CampaignMeta
              campaign={campaign}
              statusKind={campaignStatusPillKind(
                campaign.status,
                reviewsComplete,
              )}
              revisionCount={revisionCount}
            />
          </VStack>

          <ProgressMeter
            label={campaignProgressLabel(campaign)}
            value={stats.completed_reviews}
            total={stats.total_reviews}
            size="lg"
            complete={campaign.status === "completed"}
          />
        </Flex>
      </Box>

      {/* Filter row — search left, sort/group right (one row) */}
      <Box
        width="full"
        px="1xl"
        py="md"
        borderBottom="1px solid"
        borderBottomColor="border.default"
      >
        <Flex justify="space-between" align="center" gap="md" flexWrap="wrap">
          <HStack spacing="md" align="center" flexWrap="wrap">
            <Box w="220px" maxW="100%">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search"
              />
            </Box>
            <ReviewResourceFilters
              filter={reviewFilter}
              resourceOptions={resourceOptions}
              onToggleResource={toggleResource}
              onToggleRisk={toggleRisk}
              onClearResources={clearResources}
              onClearRisks={clearRisks}
            />
          </HStack>
          <ReviewFilters />
        </Flex>
      </Box>

      {/* Grouped resource list — light-grey body */}
      <Box
        width="full"
        bg="bg.elevated"
        position="relative"
        flex="1"
        minH="calc(100vh - 294px)"
        px="1xl"
        py="1xl"
      >
        {reviewsLoading ? (
          <Flex justify="center" align="center" py="2xl">
            <Spinner size="md" color="neutral.500" />
          </Flex>
        ) : searched.length === 0 ? (
          <Box py="lg">
            <Text fontSize="smd" color="text.body.tertiary">
              {hasActiveReviewFilter(reviewFilter) || search.trim()
                ? "No resources match your filters"
                : "No resources to review"}
            </Text>
          </Box>
        ) : groupBy === "status" ? (
          groups.map((group) => (
            <ResourceGroup
              key={group.key}
              group={group}
              campaignId={campaign.id}
              applicationInstructions={campaign.applicationInstructions}
              showAccountProgress={showAccountProgress}
            />
          ))
        ) : (
          <VStack spacing="md" align="stretch">
            {searched.map((review) => (
              <ResourceCard
                key={review.id}
                review={review}
                campaignId={campaign.id}
                faded={review.status === "complete"}
                showAccountProgress={showAccountProgress}
                instruction={instructionForResource(
                  campaign.applicationInstructions,
                  review.resource?.id,
                )}
              />
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default memo(CampaignDetails);
